import useHeatmapHistory from '../hooks/useHeatmapHistory'
import { createContext, useState, useEffect, useRef } from 'react'
import ApiCalls from '../../../common/ApiCalls'
import HMapModal from '../components/HMapModal'
import { FeedbackSnackbar } from '../../../components/1elements'
import { getActiveNumericFields } from '../utils/heatmapFields'
import {
  getPreferredHeatmapId,
  getVisibleHeatmaps,
  isDefaultHeatmap
} from '../utils/heatmapSelection'

// Heatmap Context
export const HMapContext = createContext()

// Get local Storage Search Settings
const getLocalSearchSettings = () => {
  try {
    return JSON.parse(window.localStorage.getItem('lastSearchDetails'))
  } catch {
    return null
  }
}

const getInitialHeatmapSearchSettings = () => {
  const mapSearchSettings = getLocalSearchSettings()
  return {
    text: '',
    option: mapSearchSettings?.option || 'Places/Account'
  }
}

const processAllMaps = (heatmapsList, favoritedItemsList, pinnedItemsList) => {
  console.log('favoritedItemsList => ', favoritedItemsList)
  console.log('pinnedItemsList =>', pinnedItemsList)
  return heatmapsList.map((map) => ({
    id: map.id,
    name: map.name,
    description: map.description,
    type: map.type,
    pin: pinnedItemsList.includes(map.id),
    star: favoritedItemsList.includes(map.id),
    created_at: map?.created_at || new Date().toISOString()
  }))
}

const getDefaultMap = (allMaps) => {
  return allMaps.find(isDefaultHeatmap)?.id || ''
}

const getIsFirstTimeOnHeatmaps = () => {
  // possible values => null, undefined, true, false
  const localStorageValue = window.localStorage.getItem('isFirstTimeOnHeatmaps')

  const actualValue = localStorageValue === 'undefined'
    ? undefined
    : JSON.parse(localStorageValue)

  return (actualValue === null || actualValue === undefined) ? true : actualValue
}

const getDontShowHeatmapInstruction = () => {
  const dontShowInstruction = JSON.parse(window.localStorage.getItem('dontShowInstruction'))
  return dontShowInstruction || false
}

const getCurrentUser = () => {
  /**
   * To be addressed later:
   *  What if userId doesnot exist ?
   */

  const currentuserId = window.localStorage.getItem('userId')
  return currentuserId
}

const processGeoZones = (geoZonesList) => {
  if (!geoZonesList) return []

  return geoZonesList
    .filter(
      (item) =>
        item?.geo_zones &&
        item?.geo_zones?.length &&
        !item.deleted_at &&
        !item.suspended_at
    )
    .map((item) => ({
      id: item.id,
      name: item.name,
      geo_zones: item.geo_zones
        .filter((zone) => zone.wkt)
        .map((zone) => ({
          id: zone.id,
          wkt: zone.wkt
        }))
    }))
}

// Heatmap Provider
export const HMapContextProvider = ({ children }) => {
  const history = useHeatmapHistory()
  const [requestStatus, setRequestStatus] = useState('')
  // Modal
  const [modalOpen, setModalOpen] = useState(false)
  const modalPropsRef = useRef({
    type: undefined,
    messageText: undefined,
    confirmFunction: undefined
  })
  // Snackbar
  const [isSnackbarOpen, setIsSnackbarOpen] = useState({})
  // Current userId
  const userIdRef = useRef(getCurrentUser())
  // Map Id for Edit
  const mapForEditRef = useRef('')
  // Dynamic Fields
  const dynamicFieldsRef = useRef([])
  // Operations
  const operationsRef = useRef([
    { value: 'count', text: 'Count' },
    { value: 'avg', text: 'Average' },
    { value: 'min', text: 'Minimum' },
    { value: 'max', text: 'Maximum' },
    { value: 'sum', text: 'Sum' }
  ])
  // DontShowInstruction
  const dontShowInstructionRef = useRef(getDontShowHeatmapInstruction())
  const handleDontShowInstruction = (checked) => {
    window.localStorage.setItem('dontShowInstruction', checked)
    dontShowInstructionRef.current = checked
  }
  // Current Map
  const [currentMap, setCurrentMap] = useState('')
  const handleCurrentMapChange = (e) => setCurrentMap(e?.target?.id)
  // Heatmap Type
  const [heatmapType, setHeatmapType] = useState('user')
  // All Heatmaps
  const [allHeatMaps, setAllHeatMaps] = useState([])

  useEffect(() => {
    const preferredMap = getPreferredHeatmapId(allHeatMaps, currentMap)
    if (preferredMap !== currentMap) setCurrentMap(preferredMap)
  }, [allHeatMaps, currentMap])
  // Serach Settings
  const [searchSettings, setSearchSettings] = useState(
    getInitialHeatmapSearchSettings
  )
  const handleSearchSettingsChange = ({ text, option }) => {
    setSearchSettings(previousSearchSettings => ({
      ...previousSearchSettings,
      ...((!!text || text === '') && { text }),
      ...((!!option || option === '') && { option })
    }))
  }

  // Geo Zones
  const [allGeoZones, setAllGeoZones] = useState('loading')
  const [heatmapGeoZone, setHeatmapGeoZone] = useState([])
  const [geoZoneState, setGeoZoneState] = useState(false)
  const toggleGeoZoneState = () => setGeoZoneState((prev) => !prev)

  /**
   * Heatmap Sort, Search
   @param {object} currentTab {
      @param {string} sortOrder 'asc' | 'desc'
      @param {string} sortType   'az' | 'date'
      @param {string} searchText the search text from the search box on heatmap tabs
   }
   */
  const [sortSearch, setSortSearch] = useState({
    active: { sortOrder: '', sortType: '', searchText: '' },
    user: { sortOrder: '', sortType: '', searchText: '' },
    business: { sortOrder: '', sortType: '', searchText: '' },
    system: { sortOrder: '', sortType: '', searchText: '' }
  })

  /**
   * Tabs (left panel)
   * @param {string} currentTab 'active' | 'user' | 'business' | 'system'
   */
  const [currentTab, setCurrentTab] = useState('active')

  useEffect(() => {
    // Load dynamic fields filtered by numeric fields
    const getDynamicFields = async () => {
      try {
        const [
          fields,
          allDbHeatmaps,
          allDbGeoZones,
          favoritedItems,
          pinnedItems
        ] = await Promise.all([
          ApiCalls().listFields(['number']),
          ApiCalls().getAllHeatMaps(),
          ApiCalls().getAllGeoZones(),
          ApiCalls().getHeatmapAttribute(userIdRef.current, 'favorite'),
          ApiCalls().getHeatmapAttribute(userIdRef.current, 'pin')
        ])

        // Dynamic Fields
        dynamicFieldsRef.current = [
          ...getActiveNumericFields(fields)
        ]

        // All Heatmap
        const allDbHeatmapsData = allDbHeatmaps?.data?.data

        // Favorited Items
        const allFavoritedItems =
          favoritedItems?.map((item) => item.item_id) || []

        // Pinned Items
        const allPinnedItems = pinnedItems?.map((item) => item.item_id) || []

        // Process Heatmaps
        let allProcessedMaps = processAllMaps(
          allDbHeatmapsData,
          allFavoritedItems,
          allPinnedItems
        )

        // set pin and favorite on default map if firsttime on heatmaps
        const defaultHeatmapId = getDefaultMap(allProcessedMaps)
        let isFirstTimeOnHeatmaps = getIsFirstTimeOnHeatmaps()

        const hasUserHeatmaps = getVisibleHeatmaps(allProcessedMaps)
          .some((item) => item.type === 'user')

        if (
          isFirstTimeOnHeatmaps &&
          defaultHeatmapId &&
          !hasUserHeatmaps &&
          userIdRef.current
        ) {
          try {
            // Add pin for default
            await ApiCalls().addHeatmapAttribute(
              userIdRef.current,
              defaultHeatmapId,
              'pin'
            )
            // Add favorite for default
            await ApiCalls().addHeatmapAttribute(
              userIdRef.current,
              defaultHeatmapId,
              'favorite'
            )

            // if no error set the flag in local storage
            window.localStorage.setItem('isFirstTimeOnHeatmaps', false)

            // set pin and favorite on defaultmap
            allProcessedMaps = allProcessedMaps.map((item) =>
              item.name.toLowerCase() === 'default heatmap'
                ? { ...item, pin: true, star: true }
                : item
            )
          } catch (e) {}
        }

        // Set the heatmaps
        setAllHeatMaps([...allProcessedMaps])

        // Select a user heatmap when available; otherwise use the default
        const initialMap = getPreferredHeatmapId(allProcessedMaps)
        setCurrentMap(initialMap)

        // Load all geozones
        const processedGeoZones = processGeoZones(allDbGeoZones)
        setAllGeoZones([...processedGeoZones])

        console.log('Context => dynamicFieldsRef', dynamicFieldsRef.current)
        console.log('Context => userIdRef', userIdRef.current)
        console.log('Context => allDbHeatmaps', allDbHeatmapsData)
        console.log('Context => NEW ALL MAPS', allProcessedMaps)
        console.log('Context => initialMap', initialMap)
        console.log('Context => searchSettings', searchSettings)
        console.log('Context => allGeoZones', processedGeoZones)
      } catch (e) {
        console.log(e)
      }
    }
    getDynamicFields()

    // XXX Fix this in the future
    // eslint-disable-next-line
  }, [])

  return (
    <HMapContext.Provider
      value={{
        history,
        requestStatusInfo: [requestStatus, setRequestStatus],
        dynamicFieldsRef,
        userIdRef,
        operationsRef,
        mapForEditRef,
        allHeatMapsList: [allHeatMaps, setAllHeatMaps],
        allGeoZoneList: [allGeoZones, setAllGeoZones],
        heatmapGeoZoneList: [heatmapGeoZone, setHeatmapGeoZone],
        geoZoneStateInfo: [geoZoneState, toggleGeoZoneState],
        searchConfig: [searchSettings, handleSearchSettingsChange],
        currentHeatmapType: [heatmapType, setHeatmapType],
        currentMapInfo: [currentMap, setCurrentMap, handleCurrentMapChange],
        dontShowInstructionConfig: [
          dontShowInstructionRef,
          handleDontShowInstruction
        ],
        sortSearchInfo: [sortSearch, setSortSearch],
        currentTabInfo: [currentTab, setCurrentTab],
        modal: [modalPropsRef, setModalOpen],
        snackbar: [isSnackbarOpen, setIsSnackbarOpen]
      }}
    >
      {children}
      {/* Snackbar */}
      <FeedbackSnackbar
        snackbar={isSnackbarOpen}
        setSnackbar={setIsSnackbarOpen}
      />
      {/* Modal */}
      <HMapModal
        open={modalOpen}
        setOpen={setModalOpen}
        type={modalPropsRef.current.type}
        messageText={modalPropsRef.current.messageText}
        confirmFunction={modalPropsRef.current.confirmFunction}
      />
    </HMapContext.Provider>
  )
}
